import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight, FaCopy, FaCheck, FaMobileAlt, FaAppStore, FaPlay, FaAndroid, FaApple,
  FaBell, FaFingerprint, FaWifi, FaBluetooth, FaShoppingCart, FaCalendarAlt, FaDumbbell,
  FaLock, FaCheckCircle, FaExclamationTriangle, FaTerminal
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function MobileAppsDetail() {
  useTheme();

  // 1. Blueprint Builder state
  const [appArchetype, setAppArchetype] = useState('ecommerce'); // ecommerce, saas, fitness, booking
  const [selectedSlot, setSelectedSlot] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const [workoutActive, setWorkoutActive] = useState(false);
  const [copiedSpec, setCopiedSpec] = useState(false);

  // Add-on switches
  const [addOnPush, setAddOnPush] = useState(false);
  const [addOnBiometric, setAddOnBiometric] = useState(false);
  const [addOnOffline, setAddOnOffline] = useState(false);
  const [addOnBLE, setAddOnBLE] = useState(false);

  // Canned push notification trigger
  const [showPushToast, setShowPushToast] = useState(false);
  const triggerNotification = () => {
    if (!addOnPush) return;
    setShowPushToast(true);
  };
  useEffect(() => {
    if (showPushToast) {
      const timer = setTimeout(() => setShowPushToast(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showPushToast]);

  // Canned biometric lock toggle
  const [biometricUnlocked, setBiometricUnlocked] = useState(false);

  // Live simulation states for simulated smartphone UI
  const [sneakerSize, setSneakerSize] = useState('9');
  const [graphPoints, setGraphPoints] = useState([12, 18, 10, 24, 15, 32, 22]);
  const [stepsCount, setStepsCount] = useState(14208);
  const [heartRate, setHeartRate] = useState(72);
  const [saasQueryLog, setSaasQueryLog] = useState('GET /api/v1/telemetry [200 OK] - 22ms');

  // Live tickers for SaaS & Fitness simulations
  useEffect(() => {
    if (appArchetype !== 'saas') return;
    const interval = setInterval(() => {
      setGraphPoints(prev => [...prev.slice(1), Math.floor(Math.random() * 25) + 10]);
      const endpoints = [
        'GET /api/v1/device/sync [200] 12ms',
        'POST /api/v1/telemetry/push [201] 28ms',
        'GET /api/v1/workspace/metrics [200] 18ms',
        'PATCH /api/v1/user/config [200] 9ms',
        'POST /api/v1/auth/token [200] 35ms'
      ];
      setSaasQueryLog(endpoints[Math.floor(Math.random() * endpoints.length)]);
    }, 1500);
    return () => clearInterval(interval);
  }, [appArchetype]);

  useEffect(() => {
    let interval;
    if (workoutActive) {
      setHeartRate(128);
      interval = setInterval(() => {
        setStepsCount(s => s + Math.floor(Math.random() * 3) + 1);
        setHeartRate(h => Math.floor(Math.random() * 8) + 124);
      }, 1000);
    } else {
      setHeartRate(72);
    }
    return () => clearInterval(interval);
  }, [workoutActive]);

  // 2. Simulated CLI Developer Journey
  const journeyStages = [
    {
      id: 'design',
      title: 'Designing Core Flows',
      subtitle: 'Figma wireframes & user navigation systems',
      cliHeader: 'bcc-mobile --task init-wireflow',
      logs: [
        'Mapping gestures: Drag-to-refresh, Swipe cards, Pinch to inspect...',
        'Creating dark/light vector palette layouts...',
        'Rendering responsive dynamic screen flows for Android & iOS heights...',
        'Output: Generated 32 vector storyboards.',
        'Done: UI components specifications frozen for engineering.'
      ],
      delay: 500
    },
    {
      id: 'core',
      title: 'SDK & Core Engineering',
      subtitle: 'Native swift/kotlin engines & hybrid cross-compiles',
      cliHeader: 'react-native run-ios --configuration Release',
      logs: [
        'Initializing native binding bridges on iOS & Android modules...',
        'Setting up offline databases (SQLite & Realm schemas)...',
        'Configuring local push notification routing tokens...',
        'Linking secure biometric Keychain & face identity modules...',
        'Core compilation: BUILD SUCCESSFUL in 84 seconds.'
      ],
      delay: 1000
    },
    {
      id: 'qa',
      title: 'Rigorous Sandbox QA',
      subtitle: 'Automated test suite & mock environment checks',
      cliHeader: 'npm run test:e2e-detox',
      logs: [
        'Running 142 integrated swipe test scenarios in emulator views...',
        'Testing mock network latency & offline caching synchronizations...',
        'Validating BLE telemetry packet payloads...',
        'Checking memory allocations: zero leaks found.',
        'Detox test results: 142 passed, 0 failed.'
      ],
      delay: 1500
    },
    {
      id: 'launch',
      title: 'Store Submission',
      subtitle: 'Apple App Store Connect & Google Play verification',
      cliHeader: 'fastlane deliver --submit-for-review',
      logs: [
        'Bundling release packages (.ipa / .aab) with matching profile certs...',
        'Generating dynamic app store marketing screenshots...',
        'Uploading metadata guides and privacy policy documentation...',
        'Awaiting Apple Store review approval pipeline...',
        'Live: App approved by Apple & Google Play Store!'
      ],
      delay: 2000
    },
    {
      id: 'monitoring',
      title: 'Lifecycle & Analytics',
      subtitle: 'Crashlytics telemetry tracking & minor versions updates',
      cliHeader: 'bcc-monitor --app-id telemetry-bcc-app',
      logs: [
        'Active connection: Crashlytics telemetry live (100% crash-free session rating)...',
        'Tracking event triggers: Checkout click, booking reservation completions...',
        'Analyzing API roundtrip latency (average response time: 24ms)...',
        'System online: Maintenance & performance tuning updates run transparently.'
      ],
      delay: 2500
    }
  ];

  const [activeStageId, setActiveStageId] = useState('design');
  const [simulationLogs, setSimulationLogs] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const startJourneySimulation = (stage) => {
    setActiveStageId(stage.id);
    setIsTyping(true);
    setSimulationLogs([]);

    stage.logs.forEach((logLine, lineIdx) => {
      setTimeout(() => {
        setSimulationLogs(prev => [...prev, logLine]);
        if (lineIdx === stage.logs.length - 1) {
          setIsTyping(false);
        }
      }, (lineIdx + 1) * 350);
    });
  };

  useEffect(() => {
    const defaultStage = journeyStages.find(s => s.id === 'design');
    if (defaultStage) startJourneySimulation(defaultStage);
  }, []);

  const handleCopySpec = () => {
    const specSummary = `BCC Native Mobile App Spec:
- Archetype: ${appArchetype.toUpperCase()}
- Add-ons selected: ${[
        addOnPush ? 'Push Notifications' : '',
        addOnBiometric ? 'Biometric FaceID Lock' : '',
        addOnOffline ? 'Offline Database Cache' : '',
        addOnBLE ? 'Bluetooth BLE Sync' : ''
      ].filter(Boolean).join(', ') || 'None'}`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>

      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden"
        style={{
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--section-divider)'
        }}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-purple-500" />

        <div className="section-container relative z-10 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
            style={{
              background: 'var(--accent-soft)',
              border: '1px solid var(--border-color)'
            }}>
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
            <span className="text-xs font-bold uppercase tracking-wider text-purple-505" style={{ color: 'rgb(168,85,247)' }}>Core Services</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            High-Performance <span className="purple-pink-gradient bg-gradient-to-r from-purple-500 to-pink-400 bg-clip-text text-transparent">Native Mobile Apps</span>.
          </h1>

          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We engineer premium iOS and Android applications. From butter-smooth gesture responses to secure biometric access, offline caches, and full App Store deployment management.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary" style={{ background: 'rgb(168, 85, 247)' }}>
              Start Your App Spec Sheet <FaArrowRight size={11} className="ml-1 inline" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHAT WE BUILD (ALTERNATING ROWS) ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Capabilities Deck</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>What We Build</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Clean native performance, robust hardware integrations, and cross-platform speed crafted exactly for your target audience.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1 — iOS Native: image left, text right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" style={{ alignItems: 'center' }}>
              <div className="rounded-3xl overflow-hidden shadow-2xl border flex items-center justify-center">
                <img src="/ios_app_preview.png" alt="iOS Mobile App Preview" className="w-full object-cover block" />
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-purple-500 mb-4">
                  <span className="w-4 h-px bg-purple-500 inline-block" /> Native iOS Platforms
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Swift Core and Apple Elegance
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We craft swift iOS apps leveraging Apple's standard framework guidelines. From CoreAnimation layers ensuring 120Hz scrolling speeds to biometric security gates and Haptic feedback.
                </p>
                <ul className="space-y-2 mb-8">
                  {['SwiftUI & native CoreAnimation pipelines', 'Secure enclave biometric keychain gates', 'WidgetKit and push routing notification tokens', 'Fully compliant with Apple Store submission parameters'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-purple-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['SwiftUI', 'FaceID Enclave', 'Dynamic Island', 'App Store Connect'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2 — Android Native: text left, image right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" style={{ alignItems: 'center' }}>
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-pink-500 mb-4">
                  <span className="w-4 h-px bg-pink-500 inline-block" /> Native Android Apps
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Kotlin Power and Play Store Scalability
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We engineer top-tier Android architectures using Jetpack Compose and native Kotlin modules. Designed for battery efficiency, robust background tasks, and diverse screen configurations.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Jetpack Compose declarative frameworks', 'WorkManager background thread processing', 'Secure sqlite internal storage caching hooks', 'Optimized bundle releases for Google Play Store'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-pink-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['Kotlin', 'Jetpack Compose', 'SQLite Sync', 'Play Store Console'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-3xl overflow-hidden shadow-2xl border flex items-center justify-center">
                <img src="/android_app_preview.png" alt="Android Native Preview" className="w-full object-cover block" />
              </div>
            </div>

            {/* Row 3 — Cross Platform: image left, text right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" style={{ alignItems: 'center' }}>
              <div className="rounded-3xl overflow-hidden shadow-2xl border flex items-center justify-center">
                <img src="/hybrid_app_preview.png" alt="Cross Platform App Preview" className="w-full object-cover block" />
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-indigo-500 mb-4">
                  <span className="w-4 h-px bg-indigo-500 inline-block" /> Hybrid & Cross-Platform
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Flutter & React Native Mastery
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Launch to iOS and Android simultaneously without compromising UX fidelity. We share up to 90% codebase while writing custom native bridge modules for hardware tasks.
                </p>
                <ul className="space-y-2 mb-8">
                  {['React Native Core & Flutter Dart compiles', 'Fast feature loops with unified codebases', 'Custom JNI Kotlin/Swift platform channels', 'Reduces engineering timelines by up to 40%'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-indigo-500 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['React Native', 'Flutter & Dart', 'Unified API', 'Custom Bridges'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 4 — Enterprise: text left, image right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" style={{ alignItems: 'center' }}>
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-purple-600 mb-4">
                  <span className="w-4 h-px bg-purple-650 inline-block" /> Enterprise Companions
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Offline Caches & Hardware Integrations
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Apps engineered for internal workflows and hardware communication (Bluetooth BLE beacons, NFC triggers, and secure telemetry data packages). Designed to run fully offline.
                </p>
                <ul className="space-y-2 mb-8">
                  {['Offline-first syncing via local SQLite/MMKV Cache', 'NFC & Bluetooth BLE hardware connections', 'Enterprise VPN security and SSO keys', 'High telemetry monitoring logs pipelines'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <FaCheck size={10} className="text-purple-600 mt-1 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {['BLE Core', 'NFC Sync', 'Offline First', 'SSO Gate'].map(tag => (
                    <span key={tag} className="text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2 rounded-3xl overflow-hidden shadow-2xl border flex items-center justify-center">
                <img src="/enterprise_mobile_preview.png" alt="Enterprise Mobile App Preview" className="w-full object-cover block" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE MOBILE BLUEPRINT BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Build Your Platform</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Interactive App Blueprint</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Select your application archetype model below to preview your screen flows and choose custom module variables in real-time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* Left Panel: Selector options (5 cols) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl relative overflow-hidden"
              style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                <h3 className="text-md font-bold mb-4 animate-fade-in" style={{ color: 'var(--text-primary)' }}>1. App Archetype</h3>

                {/* Archetype Buttons */}
                <div className="space-y-2 mb-6">
                  {[
                    { id: 'ecommerce', label: '👟 E-Commerce Store App', desc: 'Product listings, ratings, and cart flows' },
                    { id: 'saas', label: '📊 SaaS Companion app', desc: 'Workspace details and metric telemetry' },
                    { id: 'fitness', label: '💪 FitTrack Companion', desc: 'Calorie analytics and workout states' },
                    { id: 'booking', label: '🏥 Appointment Booking', desc: 'Live calendar date slot locks' }
                  ].map(arc => (
                    <button
                      key={arc.id}
                      onClick={() => {
                        setAppArchetype(arc.id);
                        setSelectedSlot('');
                        setCartCount(0);
                        setWorkoutActive(false);
                      }}
                      className={`w-full text-left p-3 rounded-2xl border transition-all duration-200 ${appArchetype === arc.id
                          ? 'border-purple-500 bg-purple-500/5 shadow-inner'
                          : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-400'
                        }`}
                    >
                      <div className="text-xs font-black" style={{ color: 'var(--text-primary)' }}>{arc.label}</div>
                      <div className="text-[10px] text-zinc-400 font-bold mt-0.5">{arc.desc}</div>
                    </button>
                  ))}
                </div>

                <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>2. Choose App Core Modules</h3>

                {/* Addon checkboxes */}
                <div className="space-y-2.5">
                  <label className="flex items-center gap-3 p-3 border rounded-2xl cursor-pointer hover:bg-zinc-850/5 dark:hover:bg-zinc-800/10 transition-all select-none"
                    style={{ borderColor: 'var(--border-color)' }}>
                    <input
                      type="checkbox"
                      checked={addOnPush}
                      onChange={(e) => {
                        setAddOnPush(e.target.checked);
                        if (e.target.checked) setShowPushToast(true);
                      }}
                      className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4 bg-zinc-800 border-zinc-700"
                    />
                    <div className="text-left">
                      <span className="text-xs font-bold block" style={{ color: 'var(--text-primary)' }}>Push Notifications (APNS / FCM)</span>
                      <span className="text-[9px] text-zinc-400 font-bold">Pop floating alerts on screen views</span>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-2xl cursor-pointer hover:bg-zinc-850/5 dark:hover:bg-zinc-800/10 transition-all select-none"
                    style={{ borderColor: 'var(--border-color)' }}>
                    <input
                      type="checkbox"
                      checked={addOnBiometric}
                      onChange={(e) => {
                        setAddOnBiometric(e.target.checked);
                        setBiometricUnlocked(false);
                      }}
                      className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4 bg-zinc-800 border-zinc-700"
                    />
                    <div className="text-left">
                      <span className="text-xs font-bold block" style={{ color: 'var(--text-primary)' }}>Keychain biometric security</span>
                      <span className="text-[9px] text-zinc-400 font-bold">Enforce FaceID PIN locks on launch</span>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-2xl cursor-pointer hover:bg-zinc-850/5 dark:hover:bg-zinc-800/10 transition-all select-none"
                    style={{ borderColor: 'var(--border-color)' }}>
                    <input
                      type="checkbox"
                      checked={addOnOffline}
                      onChange={(e) => setAddOnOffline(e.target.checked)}
                      className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4 bg-zinc-800 border-zinc-700"
                    />
                    <div className="text-left">
                      <span className="text-xs font-bold block" style={{ color: 'var(--text-primary)' }}>Offline cache active SQLite</span>
                      <span className="text-[9px] text-zinc-400 font-bold">Sync files once connection returns</span>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border rounded-2xl cursor-pointer hover:bg-zinc-850/5 dark:hover:bg-zinc-800/10 transition-all select-none"
                    style={{ borderColor: 'var(--border-color)' }}>
                    <input
                      type="checkbox"
                      checked={addOnBLE}
                      onChange={(e) => setAddOnBLE(e.target.checked)}
                      className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4 bg-zinc-800 border-zinc-700"
                    />
                    <div className="text-left">
                      <span className="text-xs font-bold block" style={{ color: 'var(--text-primary)' }}>Bluetooth BLE scan sync</span>
                      <span className="text-[9px] text-zinc-400 font-bold">Connect and dump device metrics logs</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Spec submission generation */}
              <div className="mt-8 pt-4 border-t border-dashed" style={{ borderColor: 'var(--border-color)' }}>
                <button
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl text-xs font-black transition-all bg-zinc-150 hover:bg-zinc-200 dark:bg-zinc-850 dark:hover:bg-zinc-800"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <FaCopy size={11} />
                  {copiedSpec ? 'SPECIFICATION COPIED! ✔' : 'COPY APP CONFIG SPEC'}
                </button>
              </div>
            </div>

            {/* Right Panel: Simulated Phone Canvas Frame (7 cols) */}
            <div className="lg:col-span-7 flex items-center justify-center p-4">

              {/* Glossy Smartphone chassis */}
              <div className="relative">
                {/* Outer bezel reflection shadow */}
                <div className="absolute -inset-1 rounded-[3.2rem] bg-gradient-to-b from-zinc-700 to-zinc-900 opacity-40 blur-xs pointer-events-none" />

                {/* Simulated frame */}
                <div className="w-[290px] h-[580px] rounded-[3rem] border-[10px] border-zinc-900 bg-zinc-950 shadow-2xl relative flex flex-col overflow-hidden select-none">
                  {/* Glass Glare Overlay */}
                  <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none z-30" />

                  {/* Refined Dynamic Island notch */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-black border border-zinc-800/40 z-50 flex items-center justify-between px-2.5">
                    <div className="w-2 h-2 rounded-full bg-zinc-950 border border-zinc-900 flex items-center justify-center">
                      <div className="w-0.5 h-0.5 rounded-full bg-purple-950" />
                    </div>
                    <div className="w-7 h-0.5 rounded-full bg-zinc-900" />
                  </div>

                  {/* Status Network Bar */}
                  <div className="h-9 pt-4 px-4 bg-zinc-950 flex justify-between items-center text-[7.5px] text-zinc-400 font-bold z-40 select-none">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <FaWifi size={7} />
                      <span className="text-[6.5px]">5G</span>
                      <div className="h-3 w-5.5 border border-zinc-650 rounded-sm p-0.5 flex items-center bg-zinc-900/50">
                        <div className="h-full w-3.5 bg-emerald-500 rounded-2xs" />
                      </div>
                    </div>
                  </div>

                  {/* Floating Push Notification Toast */}
                  {addOnPush && showPushToast && (
                    <div
                      onClick={triggerNotification}
                      className="absolute top-11 left-2.5 right-2.5 p-2 rounded-xl shadow-2xl z-55 flex gap-2 items-center bg-zinc-905 border border-zinc-850 text-[8px] animate-slide-in cursor-pointer"
                    >
                      <div className="w-6 h-6 rounded-lg bg-purple-500/20 border border-purple-550/30 flex items-center justify-center text-[9px] shrink-0">
                        🔔
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-purple-400">ALERT RECEIVED!</span>
                          <span className="text-[6.5px] text-zinc-500">now</span>
                        </div>
                        <p className="text-[7.5px] text-zinc-300 font-bold leading-tight mt-0.5">
                          Telemetry sync initialized successfully.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Dynamic unlocked biometric screen overlay block */}
                  {addOnBiometric && !biometricUnlocked && (
                    <div className="absolute inset-x-0 top-9 bottom-0 bg-zinc-950/98 z-45 flex flex-col items-center justify-center p-6 text-center select-none">
                      <div className="w-12 h-12 rounded-full border border-purple-500/40 flex items-center justify-center text-purple-500 mb-4 bg-purple-500/5 animate-pulse">
                        <FaFingerprint size={20} />
                      </div>
                      <span className="text-[10px] font-bold text-zinc-200">Biometric Auth Locked</span>
                      <p className="text-[8px] text-zinc-500 mt-1 max-w-[150px] leading-relaxed">
                        FaceID scan is required to authenticate database session.
                      </p>
                      <button
                        onClick={() => setBiometricUnlocked(true)}
                        className="mt-6 px-4 py-2 border rounded-full text-[9px] font-black border-purple-500/35 text-purple-400 hover:bg-purple-500/10 cursor-pointer transition-all"
                      >
                        SIMULATE FACEID MATCH
                      </button>
                    </div>
                  )}

                  {/* Main Screen Canvas inside the simulated phone */}
                  <div className="flex-1 overflow-y-auto px-4.5 py-3 bg-zinc-950 text-white scrollbar-none relative flex flex-col z-35">
                    {appArchetype === 'ecommerce' && (
                      <div className="flex-1 flex flex-col text-left animate-fade-in">
                        {/* E-comm Header */}
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[7.5px] font-bold text-zinc-500 uppercase tracking-wider">👟 SNEAKER STORE</span>
                          <div className="relative">
                            <span className="text-[10px]">🛒</span>
                            {cartCount > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 bg-purple-550 text-[6.5px] font-bold h-3 w-3 rounded-full flex items-center justify-center animate-pulse">
                                {cartCount}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Product Spotlight Showcase Box */}
                        <div className="relative rounded-2xl p-4 bg-zinc-900 border border-zinc-850 flex flex-col items-center overflow-hidden mb-3">
                          {/* Radial gradient glow backdrop */}
                          <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/15 via-transparent to-pink-500/15 pointer-events-none" />
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full bg-purple-500/5 blur-md pointer-events-none" />
                          
                          {/* Mock Shoe Graphic Vector */}
                          <div className="text-[40px] drop-shadow-[0_0_10px_rgba(168,85,247,0.3)] select-none z-10 animate-bounce-slow">
                            👟
                          </div>
                          
                          <div className="w-full text-left mt-2 relative z-10">
                            <div className="text-[9.5px] font-black tracking-tight text-white leading-none">Air Stream Pro</div>
                            <div className="text-[7.5px] text-zinc-500 font-bold mt-0.5">BCC Custom Edition</div>
                            <div className="flex gap-0.5 items-center mt-1.5 text-amber-500 text-[6px]">
                              <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                              <span className="text-zinc-550 text-[6px] font-bold ml-1">(42)</span>
                            </div>
                          </div>
                        </div>

                        {/* Sizes selector grid */}
                        <div className="mb-4">
                          <span className="text-[7.5px] font-bold text-zinc-400 block mb-1.5 uppercase">Select Size:</span>
                          <div className="flex gap-1.5">
                            {['8', '9', '10', '11'].map(sz => (
                              <button
                                key={sz}
                                onClick={() => setSneakerSize(sz)}
                                className={`flex-1 py-1 rounded-lg text-[8px] font-bold border transition-all ${sneakerSize === sz
                                    ? 'bg-white text-black border-white'
                                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                                  }`}
                              >
                                {sz}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Add to Cart Section */}
                        <div className="mt-auto flex items-center justify-between gap-3 pt-2 border-t border-zinc-900">
                          <div>
                            <span className="text-[6.5px] text-zinc-500 font-bold block uppercase leading-none">Unit Price:</span>
                            <span className="text-[11.5px] font-black text-white">$189.00</span>
                          </div>
                          <button
                            onClick={() => setCartCount(c => c + 1)}
                            className="flex-1 py-2 px-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black text-[9px] hover:brightness-110 active:scale-95 transition-all text-center"
                          >
                            ADD TO CART
                          </button>
                        </div>
                      </div>
                    )}

                    {appArchetype === 'saas' && (
                      <div className="flex-1 flex flex-col text-left animate-fade-in">
                        {/* SaaS Header */}
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[7.5px] font-bold text-zinc-500 uppercase tracking-wider">📊 WORKSPACE PORTAL</span>
                          <span className="text-[7px] text-emerald-500 font-bold flex items-center gap-0.5">
                            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-ping" />
                            LIVE SYNC
                          </span>
                        </div>

                        {/* Metrics Mini-Grid */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="p-2 border rounded-xl bg-zinc-900 border-zinc-850">
                            <span className="text-[6.5px] text-zinc-550 block font-bold uppercase leading-none">API LATENCY</span>
                            <span className="text-[11px] font-black text-purple-400 mt-1 block">22ms</span>
                          </div>
                          <div className="p-2 border rounded-xl bg-zinc-900 border-zinc-850">
                            <span className="text-[6.5px] text-zinc-550 block font-bold uppercase leading-none">ERRORS (24H)</span>
                            <span className="text-[11px] font-black text-pink-400 mt-1 block">0.02%</span>
                          </div>
                        </div>

                        {/* Cubic Bezier Line Chart Section */}
                        <div className="rounded-xl p-2.5 bg-zinc-900 border border-zinc-850 mb-3 text-center relative overflow-hidden flex flex-col justify-between h-[90px]">
                          <span className="text-[6.5px] font-bold text-zinc-500 text-left block uppercase">Request Volume / Min</span>
                          <div className="flex-1 relative flex items-end justify-center min-h-[50px] mt-1.5 select-none">
                            {/* Dotted Grid lines */}
                            <div className="absolute inset-0 flex flex-col justify-between opacity-15">
                              <div className="h-px border-b border-dashed border-zinc-650" />
                              <div className="h-px border-b border-dashed border-zinc-650" />
                              <div className="h-px border-b border-dashed border-zinc-650" />
                            </div>
                            
                            {/* Interactive SVG Path graph points */}
                            <svg className="w-full h-full overflow-visible" viewBox="0 0 240 60">
                              <defs>
                                <linearGradient id="chartGlow" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="rgba(168,85,247,0.3)" />
                                  <stop offset="100%" stopColor="rgba(168,85,247,0)" />
                                </linearGradient>
                              </defs>
                              
                              {/* Filled Gradient path under data line */}
                              <path
                                d={`M 0 60 
                                    C 20 ${60 - (graphPoints[0] || 0)}, 40 ${60 - (graphPoints[1] || 0)}, 60 ${60 - (graphPoints[1] || 0)} 
                                    C 80 ${60 - (graphPoints[2] || 0)}, 100 ${60 - (graphPoints[3] || 0)}, 120 ${60 - (graphPoints[3] || 0)} 
                                    C 140 ${60 - (graphPoints[4] || 0)}, 160 ${60 - (graphPoints[5] || 0)}, 180 ${60 - (graphPoints[5] || 0)} 
                                    C 200 ${60 - (graphPoints[6] || 0)}, 220 ${60 - (graphPoints[6] || 0)}, 240 60 Z`}
                                fill="url(#chartGlow)"
                                className="transition-all duration-500"
                              />
                              
                              {/* SVG Stroke line */}
                              <path
                                d={`M 0 ${60 - (graphPoints[0] || 0)}
                                    C 20 ${60 - (graphPoints[0] || 0)}, 40 ${60 - (graphPoints[1] || 0)}, 60 ${60 - (graphPoints[1] || 0)} 
                                    C 80 ${60 - (graphPoints[2] || 0)}, 100 ${60 - (graphPoints[3] || 0)}, 120 ${60 - (graphPoints[3] || 0)} 
                                    C 140 ${60 - (graphPoints[4] || 0)}, 160 ${60 - (graphPoints[5] || 0)}, 180 ${60 - (graphPoints[5] || 0)} 
                                    C 200 ${60 - (graphPoints[6] || 0)}, 220 ${60 - (graphPoints[6] || 0)}, 240 ${60 - (graphPoints[6] || 0)}`}
                                stroke="rgb(168,85,247)"
                                strokeWidth="1.5"
                                fill="none"
                                strokeLinecap="round"
                                className="transition-all duration-500"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Diagnostic audit logs logger */}
                        <div className="mt-auto p-2 border rounded-lg bg-black border-zinc-900 flex-1 flex flex-col justify-between min-h-[48px] max-h-[50px] relative overflow-hidden">
                          <span className="text-[5.5px] font-mono text-zinc-550 block font-bold leading-none select-none">DIAGNOSTICS:</span>
                          <div className="font-mono text-[7px] text-zinc-400 mt-1 select-text leading-tight truncate">
                            {saasQueryLog}
                          </div>
                        </div>
                      </div>
                    )}

                    {appArchetype === 'fitness' && (
                      <div className="flex-1 flex flex-col text-left animate-fade-in">
                        {/* Fitness Header */}
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[7.5px] font-bold text-zinc-500 uppercase tracking-wider">💪 ACTIVITY TRACKER</span>
                          <span className="text-[7.5px] text-pink-500 font-bold block">FIT TRACK</span>
                        </div>

                        {/* Circular progress step bar */}
                        <div className="relative rounded-2xl p-4 bg-zinc-900 border border-zinc-850 flex flex-col items-center mb-3">
                          {/* Radial activity rings */}
                          <div className="relative w-24 h-24 flex items-center justify-center">
                            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 60 60">
                              {/* Background Gray circles tracks */}
                              <circle cx="30" cy="30" r="26" stroke="#18181b" strokeWidth="4" fill="none" />
                              {/* Dynamic progress ring */}
                              <circle
                                cx="30"
                                cy="30"
                                r="26"
                                stroke="url(#pinkGlow)"
                                strokeWidth="4.5"
                                fill="none"
                                strokeDasharray="163.36"
                                strokeDashoffset={163.36 - (163.36 * Math.min(stepsCount / 15000, 1))}
                                strokeLinecap="round"
                                className="transition-all duration-1000"
                              />
                              <defs>
                                <linearGradient id="pinkGlow" x1="0" y1="0" x2="1" y2="1">
                                  <stop offset="0%" stopColor="rgb(236,72,153)" />
                                  <stop offset="100%" stopColor="rgb(168,85,247)" />
                                </linearGradient>
                              </defs>
                            </svg>
                            <div className="absolute flex flex-col items-center text-center">
                              <span className="text-[13px] font-black tracking-tight text-white leading-none">{stepsCount}</span>
                              <span className="text-[5.5px] text-zinc-550 font-bold mt-0.5 block uppercase">STEPS GOAL</span>
                            </div>
                          </div>
                        </div>

                        {/* Workout Stats indicators */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="p-2 border rounded-xl bg-zinc-900 border-zinc-850 flex flex-col justify-center">
                            <span className="text-[6.5px] text-zinc-550 block font-bold uppercase leading-none">CALORIES BURNT</span>
                            <span className="text-[10px] font-black text-pink-500 mt-1 block">428 kcal</span>
                          </div>
                          
                          {/* Heart rate monitor widget */}
                          <div className="p-2 border rounded-xl bg-zinc-900 border-zinc-850 flex items-center justify-between">
                            <div className="text-left">
                              <span className="text-[6.5px] text-zinc-550 block font-bold uppercase leading-none">HEART RATE</span>
                              <span className="text-[10px] font-black text-white mt-1 block">{heartRate} BPM</span>
                            </div>
                            <span className={`text-[12px] flex items-center leading-none select-none text-pink-550 mt-1.5 ${workoutActive ? 'animate-pulse' : ''}`}>
                              ❤️
                            </span>
                          </div>
                        </div>

                        {/* Activity Toggles */}
                        <button
                          onClick={() => setWorkoutActive(!workoutActive)}
                          className={`w-full py-2 px-3 rounded-xl font-black text-[9px] tracking-wide text-center transition-all ${workoutActive
                              ? 'bg-zinc-150 text-black hover:bg-zinc-200'
                              : 'bg-pink-550 text-white hover:brightness-110'
                            }`}
                          style={workoutActive ? {} : { backgroundColor: 'rgb(236,72,153)' }}
                        >
                          {workoutActive ? 'PAUSE WORKOUT SESSION' : 'START WORKOUT RUN'}
                        </button>
                      </div>
                    )}

                    {appArchetype === 'booking' && (
                      <div className="flex-1 flex flex-col text-left animate-fade-in">
                        {/* Booking Header */}
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-[7.5px] font-bold text-zinc-500 uppercase tracking-wider">🏥 CLINICAL BOOKING</span>
                          <span className="text-[7px] text-indigo-400 font-bold block">DR. VERMA</span>
                        </div>

                        {/* Calendar Grid Section */}
                        <div className="p-2.5 border rounded-xl bg-zinc-900 border-zinc-850 mb-3 text-center">
                          <span className="text-[6.5px] font-bold text-zinc-500 block mb-2 uppercase text-left">July 2026</span>
                          <div className="grid grid-cols-5 gap-1.5">
                            {[
                              { day: 'Mon', date: '12' },
                              { day: 'Tue', date: '13' },
                              { day: 'Wed', date: '14' },
                              { day: 'Thu', date: '15' },
                              { day: 'Fri', date: '16' }
                            ].map(item => (
                              <div key={item.date} className="flex flex-col items-center p-1 border rounded-lg bg-zinc-950 border-zinc-850/60 select-none">
                                <span className="text-[5.5px] text-zinc-550 block font-bold leading-none uppercase">{item.day}</span>
                                <span className="text-[8.5px] font-black text-white mt-1 leading-none">{item.date}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Available hours chips */}
                        <div className="mb-4">
                          <span className="text-[6.5px] font-bold text-zinc-400 block mb-1.5 uppercase">Available Slots:</span>
                          <div className="grid grid-cols-2 gap-1.5">
                            {['09:00 AM', '11:30 AM', '02:00 PM', '04:30 PM'].map(slot => (
                              <button
                                key={slot}
                                onClick={() => setSelectedSlot(slot)}
                                className={`py-1.5 px-2 rounded-lg text-[7.5px] font-black border transition-all ${selectedSlot === slot
                                    ? 'bg-indigo-650 text-white border-indigo-500 shadow-md'
                                    : 'border-zinc-800 text-zinc-450 hover:border-zinc-700'
                                  }`}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Confirmation Event Banner & checkout button */}
                        <div className="mt-auto pt-2 border-t border-zinc-900">
                          {selectedSlot ? (
                            <div className="p-2 border rounded-lg bg-indigo-500/10 border-indigo-500/25 mb-2 text-center text-indigo-400 font-bold text-[7.5px] animate-fade-in">
                              Confirming Slot at {selectedSlot}! ✔
                            </div>
                          ) : (
                            <div className="p-2 border rounded-lg bg-zinc-955 border-zinc-900/60 mb-2 text-center text-zinc-500 font-bold text-[7.5px]">
                              Select a slot time above
                            </div>
                          )}
                          <button
                            disabled={!selectedSlot}
                            onClick={() => alert(`Appointment Booked successfully for ${selectedSlot}!`)}
                            className={`w-full py-2 px-3 rounded-xl text-white font-black text-[9px] text-center transition-all ${selectedSlot
                                ? 'bg-indigo-650 hover:brightness-110 active:scale-95 leading-normal cursor-pointer text-white font-black'
                                : 'bg-zinc-850 text-zinc-650 cursor-not-allowed'
                              }`}
                          >
                            BOOK APPOINTMENT
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Simulated Phone Home swipe Indicator bar */}
                  <div className="h-4 pb-1.5 flex items-center justify-center z-40 select-none bg-zinc-955 shrink-0">
                    <div className="w-16 h-0.5 bg-zinc-800 rounded-full" />
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE DEVELOPER TIMELINE ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Technical Process</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Mobile App Development Journey</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our structural pipeline timeline milestones below to simulate how we package, test, and release app builds in real-time.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* Timeline Left Menu Selector (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between">
              <div className="space-y-3 relative before:absolute before:left-3 before:top-4 before:bottom-4 before:w-0.5 before:bg-zinc-800">
                {journeyStages.map((stage) => {
                  const isActive = activeStageId === stage.id;
                  return (
                    <button
                      key={stage.id}
                      onClick={() => startJourneySimulation(stage)}
                      className="w-full text-left relative pl-8 py-2 transition-all duration-300 select-none"
                    >
                      <div className={`absolute left-1.5 top-3.5 w-3.5 h-3.5 rounded-full border-2 transition-all duration-300 ${isActive
                          ? 'bg-purple-500 border-purple-500 scale-125 shadow-lg'
                          : 'bg-zinc-950 border-zinc-700'
                        }`} />
                      <h4 className={`text-sm font-black transition-all ${isActive ? 'text-purple-400' : 'text-zinc-400'}`}>
                        {stage.title}
                      </h4>
                      <p className="text-[10px] text-zinc-500 font-bold mt-0.5">
                        {stage.subtitle}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timeline Right Terminal console (7 columns) */}
            <div className="lg:col-span-7">
              <div className="border rounded-2xl p-4 h-full min-h-[300px] flex flex-col justify-between shadow-2xl relative bg-zinc-950/90"
                style={{ borderColor: 'var(--border-color)' }}>

                {/* Terminal Header */}
                <div className="flex items-center justify-between border-b pb-2 mb-3 border-zinc-850">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span className="text-[9px] font-mono text-zinc-500 ml-1.5">bcc-developer-core.sh</span>
                  </div>
                  <FaTerminal className="text-zinc-650" size={10} />
                </div>

                {/* Terminal Body */}
                <div className="flex-1 font-mono text-[9.5px] leading-relaxed text-zinc-300 space-y-2 select-text text-left">
                  <div className="flex items-center gap-1 text-purple-400 font-bold">
                    <span>$</span>
                    <span>{journeyStages.find(s => s.id === activeStageId)?.cliHeader || 'bcc-mobile'}</span>
                  </div>

                  <div className="space-y-1 opacity-90 select-text">
                    {simulationLogs.map((log, index) => (
                      <div key={index} className="flex gap-2">
                        <span className="text-zinc-500 select-none">✔</span>
                        <span>{log}</span>
                      </div>
                    ))}
                    {isTyping && (
                      <div className="flex gap-1.5 items-center">
                        <span className="w-1 h-3.5 bg-purple-500 animate-pulse" />
                        <span className="text-[7.5px] text-zinc-500 font-bold">locating binding targets...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Terminal Footer */}
                <div className="border-t pt-2 mt-4 flex justify-between items-center text-[7.5px] font-mono text-zinc-500 border-zinc-850">
                  <span>STAGE: {activeStageId.toUpperCase()}</span>
                  <span>CLI ENGINES ACTIVE</span>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── SECURITY & QUALITY STANDARDS ── */}
      <section className="py-24 px-6" style={{ background: 'var(--bg-surface)' }}>
        <div className="section-container max-w-5xl">
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Our Standards</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Designed for Retention & Scale</h2>
            <p className="subheadline max-w-2xl mx-auto">
              We compile code following key enterprise guidelines ensuring app stability, secure logins, and lightning load speeds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Standard 1: Performance */}
            <div className="p-6 border rounded-3xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div className="h-9 w-9 rounded-xl flex items-center justify-center text-purple-500 mb-4 bg-purple-500/10">
                🚀
              </div>
              <h4 className="text-md font-bold mb-2 text-left" style={{ color: 'var(--text-primary)' }}>120Hz Animation Frametimes</h4>
              <p className="text-xs leading-relaxed text-left" style={{ color: 'var(--text-secondary)' }}>
                We structure interface transitions utilizing native hardware compilation pipelines to avoid stutter, dropped frames, and memory overhead.
              </p>
            </div>

            {/* Standard 2: Biometric Encryption */}
            <div className="p-6 border rounded-3xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div className="h-9 w-9 rounded-xl flex items-center justify-center text-pink-505 mb-4 bg-pink-500/10" style={{ color: 'rgb(236,72,153)' }}>
                🔒
              </div>
              <h4 className="text-md font-bold mb-2 text-left" style={{ color: 'var(--text-primary)' }}>Secure Enclave Identity</h4>
              <p className="text-xs leading-relaxed text-left" style={{ color: 'var(--text-secondary)' }}>
                We store session locks and API keys using device iOS Keychain and Android Keystore standards to prevent local database tamper hacks.
              </p>
            </div>

            {/* Standard 3: App Store Optimization */}
            <div className="p-6 border rounded-3xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div className="h-9 w-9 rounded-xl flex items-center justify-center text-indigo-505 mb-4 bg-indigo-500/10" style={{ color: 'rgb(99,102,241)' }}>
                📲
              </div>
              <h4 className="text-md font-bold mb-2 text-left" style={{ color: 'var(--text-primary)' }}>ASO & Store Rankings Ready</h4>
              <p className="text-xs leading-relaxed text-left" style={{ color: 'var(--text-secondary)' }}>
                We packaging assets to meet strict Apple guidelines ensuring faster reviews and configure keyword SEO index schema metadata.
              </p>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
